import { jsx, css } from '@emotion/react';
import _ from 'lodash';
import { Component } from 'react';
import { Provider } from 'react-redux';

import { TalkToAnExpert } from './TalkToAnExpert';
import { AttentionBarPicker } from '../OC/PageBuilder';
import Reusable from '../OC/PageBuilder/Reusable';
import AttentionBarText from '../OC/oc-attention-bar-text';
import Cta from '../OC/oc-cta';
import Applications from '../OC/sections/oc-applications';
import BuildingEnclosureSolutionsBanner from '../OC/sections/oc-building-enclosure-solutions-banner';
import Certifications from '../OC/sections/oc-certifications';
import ContactUs from '../OC/sections/oc-contact-us';
import EbooksAndGuides from '../OC/sections/oc-ebooks-and-guides';
import PerformanceAttributes from '../OC/sections/oc-performance-attributes';
import PrimaryCTA from '../OC/sections/oc-primary-cta';
import ProductFeatures from '../OC/sections/oc-product-features';
import RegionalAvailability from '../OC/sections/oc-regional-availability';
import RelatedProducts from '../OC/sections/oc-related-products';
import Resources from '../OC/sections/oc-resources';
import SpecificationsAndLiterature from '../OC/sections/oc-specifications-and-literature';
import StickyNav from '../OC/sections/oc-sticky-nav';
import TechnicalInformationTable from '../OC/sections/oc-technical-information-table';
import getExtension from '../OC/utilities/file-extension';
import PriceSpider from '../PriceSpider';
import { store } from '../Store';
import { connectLocation } from '../location/LocationConnectors';

const stylesOverrides = css`
  h2,
  .h2 {
    margin-bottom: 24px;
  }

  h6,
  .h6 {
    text-transform: capitalize;
  }

  section {
    &.border-bottom {
      border-bottom: 2px solid #000;

      @media (min-width: 1200px) {
        padding-bottom: 72px;
      }

      @media (min-width: 768px) {
        padding-bottom: 32px;
      }

      padding-bottom: 15px;
    }

    &.grey-bg {
      background: #e6e6e6;
    }
  }
`;

const frenchOverrides = css`
  h6,
  .h6 {
    text-transform: initial;
  }
`;

const featureKey = product => product.category.replace(/\//g, '_');

const getDocuments = data => {
  // First determine where the documents are located
  const documents_source = _.get(data, `${data.category}_product_document.documents`, data.documents);
  // Split between featured and other documents
  const {
    featured_document_1 = [],
    featured_document_2 = [],
    documents_featured_document_1 = [],
    documents_featured_document_2 = [],
    ...documents
  } = documents_source;

  // Get value for each featured document, no matter which key it is under
  const fd_1 = featured_document_1[0] || documents_featured_document_1[0];
  const fd_2 = featured_document_2[0] || documents_featured_document_2[0];
  // Add description from yet another place, but only if document exists
  if (fd_1) fd_1.description = _.get(data, 'featured_document_1_description.value');
  if (fd_2) fd_2.description = _.get(data, 'featured_document_2_description.value');

  return {
    types: data.document_types,
    featured: [fd_1, fd_2],
    documents,
  };
};

const bulletPointFilters = feature =>
  !feature.youtubeId &&
  !feature.images?.banner?.id &&
  feature.name?.length &&
  feature.copy.value.length &&
  feature.copy.value !== 'SECTION_HEADER';

const getPerformanceAttributes = data => {
  const keyFeatures = _.get(data, `${featureKey(data)}_key_feature.${featureKey(data)}_key_features`, []);
  const marqueeFeatures = _.get(data, `${featureKey(data)}_marquee_feature.${featureKey(data)}_marquee_features`, []);
  const bulletPoints = marqueeFeatures.filter(bulletPointFilters);
  const withMedia = marqueeFeatures.filter(f => f.youtubeId || f.images?.banner?.id);

  return {
    keyFeatures,
    bulletPoints,
    withMedia,
  };
};

const ApplicationContactUs = ({ localeCode, data = {} }) => {
  const { applications = [] } = data;
  const isCompositeLumber = applications.some(app => app.uid == 'composite_lumber');
  if (isCompositeLumber) {
    return <Reusable language={localeCode} name="Composites Lumber Contact Us" path="composites" />;
  }

  return <ContactUs />;
};

const getTechnicalInformation = data => {
  const marqueeFeatures = _.get(
    data,
    `${featureKey(data)}_marquee_tech_feature.${featureKey(data)}_marquee_tech_features`,
    []
  );
  return {
    intro: _.get(data, 'technical_information_description.value'),
    contents: _.get(data, 'technical_information_table.value'),
    bulletPoints: marqueeFeatures.filter(f => !f.youtubeId && !f.images?.banner?.id),
    withMedia: marqueeFeatures.filter(f => f.youtubeId || f.images?.banner?.id),
  };
};

const getRegionalAvailabilityData = data => ({
  description: _.get(data, 'availability_details_description.value'),
  title: _.get(data, 'availability_details_table_name.value'),
  table: _.get(data, 'availability_details_table.value'),
});

const ProductPDP = connectLocation(
  ({
    t,
    locale: { code: localeCode },
    data,
    building_enclosure_solutions_banner,
    showFoamularNgxBanner,
    showThermafiberBanner,
  }) => {
    const isPinkbar = data.uid === 'flatworkfiberglassrebar';
    const { intro_video = [], ...videos } = data.videos || {};
    const resources = [
      ...Object.values(videos || {}).reduce((a, v) => (a = [...a, ...v]), []),
      ..._.get(data, 'images.installation', []),
    ].sort((a, b) => a.sort_order - b.sort_order);
    const hasResources = resources.length > 0;
    const performanceAttributes = getPerformanceAttributes(data);
    const hasPerformanceAttributes = !!Object.values(performanceAttributes).find(pa => pa.length);
    const technicalInformation = getTechnicalInformation(data);
    const hasTechnicalInformation =
      technicalInformation.intro || technicalInformation.heading || technicalInformation.contents;
    const regionalAvailabilityData = getRegionalAvailabilityData(data);
    const hasRegionalAvailability = regionalAvailabilityData.description || regionalAvailabilityData.table;
    const pricespider_upc = _.get(data, 'pricespider_upc.value');
    const primaryCTA = _.get(data, 'documents.primary_cta[0]') || data.primary_cta;
    const introDescription = _.get(data, 'intro_copy.value') || _.get(data, 'teaser_copy.value');
    const primaryCtaBlock = {
      heading: _.get(data, 'intro_copy_head.value'),
      prehead: _.get(data, 'intro_copy_prehead.value'),
      description: introDescription,
      image: _.get(data, 'images.intro_image', ''),
      video: _.get(intro_video, '[0].url'),
    };
    let primaryCtaButton;
    if (primaryCTA) {
      primaryCtaButton =
        primaryCTA.url == '#talk-to-an-expert' ? (
          <Cta
            data-track="tab-click"
            className="oc-cta-button"
            navigateTo="talk-to-an-expert"
            data-track-tab-name="Talk To An Expert"
            data-track-element-location="main section CTA"
          >
            {primaryCTA.value}
          </Cta>
        ) : (
          <Cta
            target="_blank"
            className="oc-cta-button"
            href={primaryCTA.url}
            data-track="contact-us"
            data-track-element-location="main section CTA"
            data-track="file-download"
            data-track-file-type={getExtension(primaryCTA.url)}
            data-track-file-name={primaryCTA.title}
            data-track-doc-type={primaryCTA.category}
            data-track-pubid={primaryCTA.publication_id}
          >
            {primaryCTA.value}
          </Cta>
        );
    }
    // Pricespider overrider the default CTA
    if (pricespider_upc) {
      primaryCtaButton = <PriceSpider pricespider_upc={pricespider_upc} />;
    }

    // Pinkbar Attention Bar CTA
    let attentionBarCta;
    if (isPinkbar) {
      attentionBarCta = (
        <Cta
          className="oc-cta-button"
          data-track="learn-more"
          data-track-element-location="main section CTA"
          data-track-destination="PinkBar Rebar Construction Calculator"
          href="/composites/pinkbar-vs-steel-rebar"
        >
          <span>{t('pinkbar.stickynav_cta')}</span>
        </Cta>
      );
    }

    const tProductWithFallback = str => t(`${data.category}.product.${str}`, { default: t(`product.${str}`) });

    return (
      <div css={[stylesOverrides, localeCode === 'fr-CA' ? frenchOverrides : null]}>
        <StickyNav cta={primaryCtaButton}>
          {primaryCtaBlock.description ? (
            <PrimaryCTA {...primaryCtaBlock} cta={primaryCtaButton} className="border-bottom" excludeFromStickyNav />
          ) : null}
          {isPinkbar ? (
            <AttentionBarText
              heading={t('product.pinkbar_calculator.heading')}
              text={t('product.pinkbar_calculator.attention_bar_text')}
            >
              {attentionBarCta}
            </AttentionBarText>
          ) : null}
          {showFoamularNgxBanner ? (
            <AttentionBarPicker
              settings={{ attentionBarType: 'noImage' }}
              layout="custom"
              copy={{
                heading: t('foamular_xps.ngx_attention_title'),
                content: t('foamular_xps.ngx_attention_content'),
              }}
              cta={{
                destination: 'link',
                url: '/insulation/commercial/foamular-ngx',
                text: t('foamular_xps.ngx_attention_button'),
                analytics: {
                  track: 'learn-more',
                  'element-location': 'main section CTA',
                  destination: 'View Products',
                },
              }}
            />
          ) : null}
          {showThermafiberBanner ? (
            <Reusable language={localeCode} name="Thermafiber - Life Safety Banner" path="insulation" />
          ) : null}
          <SpecificationsAndLiterature
            heading={tProductWithFallback('specifications_and_literature')}
            {...getDocuments(data)}
          />
          {building_enclosure_solutions_banner ? <BuildingEnclosureSolutionsBanner /> : null}
          {data.features ? (
            <ProductFeatures
              heading={tProductWithFallback('product_features')}
              intro={_.get(data, 'feature_copy.value')}
              features={data.features}
              className="border-bottom"
            />
          ) : null}
          {data.certifications ? (
            <Certifications
              heading={tProductWithFallback('certifications')}
              certifications={data.certifications}
              className={hasPerformanceAttributes ? null : `border-bottom`}
            />
          ) : null}
          {hasPerformanceAttributes ? (
            <PerformanceAttributes
              heading={tProductWithFallback('performance_attributes')}
              {...performanceAttributes}
            />
          ) : null}
          {hasResources ? (
            <Resources heading={tProductWithFallback('resources')} resources={resources} className="border-bottom" />
          ) : null}
          {hasTechnicalInformation ? (
            <TechnicalInformationTable
              heading={tProductWithFallback('technical_information_table')}
              {...technicalInformation}
              className="border-bottom"
            />
          ) : null}
          {hasRegionalAvailability ? (
            <RegionalAvailability
              className="border-bottom"
              heading={t('product.regional_availability')}
              {...regionalAvailabilityData}
            />
          ) : null}
          {data.applications ? (
            <Applications
              heading={tProductWithFallback('applications')}
              intro={_.get(data, 'application_copy.value')}
              applications={data.applications}
              className="border-bottom"
            />
          ) : null}
          {data.related_products && data.related_products.length > 0 ? (
            <RelatedProducts
              productCategory={data.category}
              heading={tProductWithFallback('related_products')}
              products={data.related_products}
              className="border-bottom"
            />
          ) : null}
          {data.ebooks ? (
            <EbooksAndGuides
              heading={tProductWithFallback('ebooks_and_guides')}
              ebooks={data.ebooks}
              className="border-bottom"
            />
          ) : null}
          {data.business_contacts ? (
            <TalkToAnExpert
              heading={tProductWithFallback('talk_to_an_expert.talk_to_an_expert')}
              body={tProductWithFallback('talk_to_an_expert.text')}
              list={data.business_contacts}
              className="border-bottom"
            />
          ) : null}
          <ApplicationContactUs data={data} localeCode={localeCode} />
        </StickyNav>
      </div>
    );
  }
);

class ProductPDPContainer extends Component {
  render() {
    return (
      <Provider store={store}>
        <ProductPDP {...this.props} />
      </Provider>
    );
  }
}

export default ProductPDPContainer;
